import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withModifiers as _withModifiers, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "d-flex justify-space-between align-center mb-6" }
const _hoisted_2 = { class: "d-flex align-center mt-2" }
const _hoisted_3 = { class: "text-center" }
const _hoisted_4 = { class: "text-h4 font-weight-bold text-primary" }
const _hoisted_5 = { class: "text-center" }
const _hoisted_6 = { class: "text-h4 font-weight-bold text-success" }
const _hoisted_7 = { class: "text-center" }
const _hoisted_8 = { class: "text-h4 font-weight-bold text-info" }
const _hoisted_9 = { class: "text-center" }
const _hoisted_10 = { class: "text-h4 font-weight-bold text-purple" }
const _hoisted_11 = { class: "report-preview pa-4" }
const _hoisted_12 = { class: "d-flex align-center mb-6" }
const _hoisted_13 = { class: "text-h6 font-weight-bold" }
const _hoisted_14 = { class: "text-body-2 text-medium-emphasis" }
const _hoisted_15 = {
  key: 0,
  class: "d-flex justify-center align-center pa-12"
}
const _hoisted_16 = {
  key: 1,
  class: "text-center pa-12"
}
const _hoisted_17 = {
  key: 2,
  class: "report-content"
}
const _hoisted_18 = { class: "text-h5 font-weight-bold mb-3" }
const _hoisted_19 = { class: "text-h5 font-weight-bold text-primary mb-3" }
const _hoisted_20 = { class: "text-body-2 text-medium-emphasis" }
const _hoisted_21 = {
  key: 0,
  class: "mb-6"
}
const _hoisted_22 = {
  key: 2,
  class: "text-center pa-6"
}
const _hoisted_23 = { class: "mt-6" }
const _hoisted_24 = { class: "text-h5 font-weight-bold text-primary" }
const _hoisted_25 = { class: "text-h5 font-weight-bold text-success" }
const _hoisted_26 = { class: "text-h5 font-weight-bold text-info" }
const _hoisted_27 = { class: "text-h5 font-weight-bold text-purple" }
const _hoisted_28 = { class: "text-body-2 mt-6 pa-3 bg-grey-lighten-5 rounded" }

import { ref, computed, onMounted } from 'vue'
import OxygenChart from '@/components/charts/OxygenChart.vue'
import TemperatureOxygenChart from '@/components/charts/TemperatureOxygenChart.vue'
import { useDeviceStore } from '@/views/Devices/store'
import { useMeasurementsStore } from '@/views/DeviceSamples/store'
import type { Device } from '@/views/Devices/models/device'
import type { DeviceSample } from '@/views/DeviceSamples/models/deviceSample'
import { useDeviceLoader } from '@/composables/useDeviceLoader'
import { useDeviceSensorManager } from '@/composables/useDeviceSensorManager'
import {
    generateGrowthReport,
    generateSensorReport,
    generateBatteryReport,
    generateComparativeReport,
    generateExecutiveReport
} from '@/config/reportGenerator'

// Stores

export default /*@__PURE__*/_defineComponent({
  __name: 'ReportsView',
  setup(__props) {

const deviceStore = useDeviceStore()
const measurementsStore = useMeasurementsStore()
const deviceLoader = useDeviceLoader()
const sensorManager = useDeviceSensorManager()

// Estados reactivos
const devices = ref<Device[]>([])
const allMeasurements = ref<DeviceSample[]>([])
const loading = ref(false)
const generatingReport = ref<string | null>(null)
const error = ref<string | null>(null)
const sensorDataForCharts = ref<Array<{ timestamp: string, humidity: number, oxygen: number, temperature: number, batteryLevel: number }>>([])

// Reportes disponibles para generar con datos reales
const availableReports = [
    {
        id: 'growth',
        name: 'Reporte de Crecimiento',
        description: 'Análisis completo del crecimiento de raíces con datos de todas las mediciones analizadas',
        type: 'Crecimiento',
        icon: 'mdi-chart-line',
        color: '#4CAF50',
        generate: () => generateGrowthReport(devices.value, allMeasurements.value)
    },
    {
        id: 'sensors',
        name: 'Informe de Sensores',
        description: 'Estado de los dispositivos, cantidad de muestras y estado de conexión',
        type: 'Sensores',
        icon: 'mdi-thermometer',
        color: '#2196F3',
        generate: () => generateSensorReport(devices.value, allMeasurements.value)
    },
    {
        id: 'battery',
        name: 'Reporte de Batería',
        description: 'Niveles de batería, señal y mantención de todos los dispositivos',
        type: 'Batería',
        icon: 'mdi-battery',
        color: '#FF9800',
        generate: () => generateBatteryReport(devices.value)
    },
    {
        id: 'comparative',
        name: 'Análisis Comparativo',
        description: 'Comparación de rendimiento entre dispositivos: crecimiento, salud y raíces',
        type: 'Comparativo',
        icon: 'mdi-compare',
        color: '#9C27B0',
        generate: () => generateComparativeReport(devices.value, allMeasurements.value)
    },
    {
        id: 'executive',
        name: 'Dashboard Ejecutivo',
        description: 'Resumen ejecutivo con KPIs clave, tasas de actividad y top dispositivos',
        type: 'Ejecutivo',
        icon: 'mdi-account-tie',
        color: '#795548',
        generate: () => generateExecutiveReport(devices.value, allMeasurements.value)
    }
]

const selectedReport = ref(availableReports[0])

const selectReport = (report: typeof availableReports[0]) => {
    selectedReport.value = report
}

const handleGenerate = async (report: typeof availableReports[0]) => {
    generatingReport.value = report.id
    try {
        // Pequeño delay para que se vea el loading
        await new Promise(r => setTimeout(r, 300))
        report.generate()
    } finally {
        generatingReport.value = null
    }
}

// Computed: métricas reales
const deviceMetrics = computed(() => {
    const managedMeasurements = allMeasurements.value.filter(m => m.managed)
    return {
        totalDevices: devices.value.length,
        activeDevices: devices.value.filter(d => d.active).length,
        totalMeasurements: allMeasurements.value.length,
        analyzedMeasurements: managedMeasurements.length,
        averageOxygen: 0,
        averageBattery: 0,
        totalNewRoots: managedMeasurements.reduce((s, m) => s + m.newRootsCount, 0),
        averageGrowthPercentage: managedMeasurements.length > 0
            ? Math.round(managedMeasurements.reduce((s, m) => s + m.growthPercent, 0) / managedMeasurements.length * 10) / 10
            : 0
    }
})

// Computed: datos de sensores
const sensorData = computed(() => {
    if (sensorDataForCharts.value.length > 0) return sensorDataForCharts.value
    return []
})

// Computed: últimas mediciones analizadas
const growthMeasurements = computed(() => {
    return allMeasurements.value
        .filter(m => m.managed && m.growthPercent != null)
        .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
        .slice(0, 5)
        .map(m => {
            const device = devices.value.find(d => d.id === m.deviceId)
            return {
                deviceName: device?.name || m.deviceId.slice(0, 12),
                growthPercentage: m.growthPercent,
                newRoots: m.newRootsCount,
                healthScore: m.rootHealthScore,
                date: new Date(m.time).toLocaleDateString('es-CL')
            }
        })
})

// Load real data
const loadRealData = async () => {
    try {
        loading.value = true
        await deviceLoader.loadDevicesByRole()
        devices.value = deviceStore.devices

        await measurementsStore.getMeasurements()
        allMeasurements.value = measurementsStore.deviceSamples

        if (devices.value.length > 0) {
            try {
                const firstDeviceId = devices.value[0].id
                const data = await sensorManager.loadDeviceSensorData(firstDeviceId)
                if (data.temperatureOxygenData && data.temperatureOxygenData.length > 0) {
                    sensorDataForCharts.value = data.temperatureOxygenData.map(d => ({
                        timestamp: new Date(d.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
                        humidity: 0,
                        oxygen: d.oxygen || 0,
                        temperature: d.temperature || 0,
                        batteryLevel: 0
                    }))
                }
            } catch {
                // Continuar sin datos de sensores
            }
        }
    } catch (err) {
        error.value = err instanceof Error ? err.message : 'Error cargando datos'
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    loadRealData()
})

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item_subtitle = _resolveComponent("v-list-item-subtitle")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_table = _resolveComponent("v-table")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, { fluid: "" }, {
    default: _withCtx(() => [
      _createVNode(_component_v_row, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                _cache[2] || (_cache[2] = _createElementVNode("div", null, [
                  _createElementVNode("h1", { class: "text-h4 font-weight-bold" }, "Reportes del Fotorizotron"),
                  _createElementVNode("p", { class: "text-body-1 text-medium-emphasis" }, " Genera reportes PDF con los datos reales de tus dispositivos y mediciones ")
                ], -1)),
                (loading.value)
                  ? (_openBlock(), _createBlock(_component_v_chip, {
                      key: 0,
                      color: "primary",
                      variant: "tonal"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_progress_circular, {
                          indeterminate: "",
                          size: "16",
                          width: "2",
                          class: "mr-2"
                        }),
                        _cache[1] || (_cache[1] = _createTextVNode(" Cargando datos... "))
                      ]),
                      _: 1
                    }))
                  : (_openBlock(), _createBlock(_component_v_chip, {
                      key: 1,
                      color: "success",
                      variant: "tonal"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(deviceMetrics.value.totalDevices) + " dispositivos • " + _toDisplayString(deviceMetrics.value.totalMeasurements) + " mediciones ", 1)
                      ]),
                      _: 1
                    }))
              ])
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_row, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, {
            cols: "12",
            md: "5"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card, {
                flat: "",
                class: "rounded-lg"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center px-4" }, {
                    default: _withCtx(() => [
                      _cache[3] || (_cache[3] = _createElementVNode("div", { class: "text-h6 font-weight-bold" }, "Generar Reportes", -1)),
                      _createVNode(_component_v_chip, {
                        color: "primary",
                        size: "small"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(availableReports.length) + " tipos", 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_text, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_list, null, {
                        default: _withCtx(() => [
                          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(availableReports, (report) => {
                            return _createVNode(_component_v_list_item, {
                              key: report.id,
                              class: _normalizeClass([{ 'selected-report': selectedReport.value.id === report.id }, "mb-2 rounded-lg"]),
                              onClick: ($event: any) => (selectReport(report))
                            }, {
                              prepend: _withCtx(() => [
                                _createVNode(_component_v_avatar, {
                                  color: report.color + '20',
                                  size: "48",
                                  class: "mr-3"
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_icon, {
                                      color: report.color,
                                      size: "24"
                                    }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(report.icon), 1)
                                      ]),
                                      _: 2
                                    }, 1032, ["color"])
                                  ]),
                                  _: 2
                                }, 1032, ["color"])
                              ]),
                              append: _withCtx(() => [
                                _createVNode(_component_v_btn, {
                                  icon: "",
                                  size: "small",
                                  color: report.color,
                                  loading: generatingReport.value === report.id,
                                  disabled: loading.value || generatingReport.value !== null,
                                  onClick: _withModifiers(($event: any) => (handleGenerate(report)), ["stop"])
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_icon, null, {
                                      default: _withCtx(() => _cache[5] || (_cache[5] = [
                                        _createTextVNode("mdi-download")
                                      ])),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_tooltip, {
                                      activator: "parent",
                                      location: "top"
                                    }, {
                                      default: _withCtx(() => _cache[6] || (_cache[6] = [
                                        _createTextVNode("Generar y descargar PDF")
                                      ])),
                                      _: 1
                                    })
                                  ]),
                                  _: 2
                                }, 1032, ["color", "loading", "disabled", "onClick"])
                              ]),
                              default: _withCtx(() => [
                                _createVNode(_component_v_list_item_title, { class: "font-weight-medium" }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(report.name), 1)
                                  ]),
                                  _: 2
                                }, 1024),
                                _createVNode(_component_v_list_item_subtitle, { class: "mt-1 text-wrap" }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(report.description), 1)
                                  ]),
                                  _: 2
                                }, 1024),
                                _createElementVNode("div", _hoisted_2, [
                                  _createVNode(_component_v_chip, {
                                    size: "x-small",
                                    color: report.color,
                                    variant: "outlined",
                                    class: "mr-2"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(report.type), 1)
                                    ]),
                                    _: 2
                                  }, 1032, ["color"]),
                                  _cache[4] || (_cache[4] = _createElementVNode("span", { class: "text-caption text-medium-emphasis" }, "PDF", -1))
                                ])
                              ]),
                              _: 2
                            }, 1032, ["class", "onClick"])
                          }), 64))
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_card, {
                flat: "",
                class: "rounded-lg mt-6"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center px-4" }, {
                    default: _withCtx(() => [
                      _cache[8] || (_cache[8] = _createElementVNode("div", { class: "text-h6 font-weight-bold" }, "Métricas del Sistema", -1)),
                      _createVNode(_component_v_btn, {
                        variant: "text",
                        color: "primary",
                        size: "small",
                        onClick: loadRealData,
                        loading: loading.value
                      }, {
                        default: _withCtx(() => _cache[7] || (_cache[7] = [
                          _createTextVNode(" Actualizar ")
                        ])),
                        _: 1
                      }, 8, ["loading"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_text, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_row, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_col, {
                            cols: "6",
                            md: "3"
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_3, [
                                _createElementVNode("div", _hoisted_4, _toDisplayString(deviceMetrics.value.totalDevices), 1),
                                _cache[9] || (_cache[9] = _createElementVNode("div", { class: "text-caption text-medium-emphasis" }, "Dispositivos", -1))
                              ])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "6",
                            md: "3"
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_5, [
                                _createElementVNode("div", _hoisted_6, _toDisplayString(deviceMetrics.value.activeDevices), 1),
                                _cache[10] || (_cache[10] = _createElementVNode("div", { class: "text-caption text-medium-emphasis" }, "Activos", -1))
                              ])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "6",
                            md: "3"
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_7, [
                                _createElementVNode("div", _hoisted_8, _toDisplayString(deviceMetrics.value.analyzedMeasurements), 1),
                                _cache[11] || (_cache[11] = _createElementVNode("div", { class: "text-caption text-medium-emphasis" }, "Analizadas", -1))
                              ])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "6",
                            md: "3"
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_9, [
                                _createElementVNode("div", _hoisted_10, _toDisplayString(deviceMetrics.value.totalNewRoots), 1),
                                _cache[12] || (_cache[12] = _createElementVNode("div", { class: "text-caption text-medium-emphasis" }, "Raíces Nuevas", -1))
                              ])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "7"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card, {
                flat: "",
                class: "rounded-lg"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center px-4" }, {
                    default: _withCtx(() => [
                      _cache[13] || (_cache[13] = _createElementVNode("div", { class: "text-h6 font-weight-bold" }, "Vista Previa de Datos", -1)),
                      _createVNode(_component_v_btn, {
                        "prepend-icon": "mdi-download",
                        color: "primary",
                        size: "small",
                        loading: generatingReport.value === selectedReport.value.id,
                        disabled: loading.value || generatingReport.value !== null,
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (handleGenerate(selectedReport.value)))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(" Generar " + _toDisplayString(selectedReport.value.name), 1)
                        ]),
                        _: 1
                      }, 8, ["loading", "disabled"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_text, null, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_11, [
                        _createElementVNode("div", _hoisted_12, [
                          _createVNode(_component_v_avatar, {
                            color: selectedReport.value.color + '20',
                            size: "48",
                            class: "mr-3"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_icon, {
                                color: selectedReport.value.color,
                                size: "24"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(selectedReport.value.icon), 1)
                                ]),
                                _: 1
                              }, 8, ["color"])
                            ]),
                            _: 1
                          }, 8, ["color"]),
                          _createElementVNode("div", null, [
                            _createElementVNode("div", _hoisted_13, _toDisplayString(selectedReport.value.name), 1),
                            _createElementVNode("div", _hoisted_14, _toDisplayString(selectedReport.value.description), 1)
                          ])
                        ]),
                        _createVNode(_component_v_divider, { class: "mb-6" }),
                        (loading.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                              _createVNode(_component_v_progress_circular, {
                                indeterminate: "",
                                color: "primary",
                                size: "48"
                              })
                            ]))
                          : (allMeasurements.value.length === 0 && devices.value.length === 0)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                _createVNode(_component_v_icon, {
                                  size: "64",
                                  color: "grey-lighten-2"
                                }, {
                                  default: _withCtx(() => _cache[14] || (_cache[14] = [
                                    _createTextVNode("mdi-database-off")
                                  ])),
                                  _: 1
                                }),
                                _cache[15] || (_cache[15] = _createElementVNode("div", { class: "text-h6 mt-3 text-medium-emphasis" }, "Sin datos disponibles", -1)),
                                _cache[16] || (_cache[16] = _createElementVNode("div", { class: "text-body-2 text-grey" }, "Los reportes se generarán cuando haya dispositivos y mediciones", -1))
                              ]))
                            : (_openBlock(), _createElementBlock("div", _hoisted_17, [
                                _cache[29] || (_cache[29] = _createElementVNode("div", { class: "text-h6 mb-4" }, "Datos Actuales de Sensores", -1)),
                                _createVNode(_component_v_row, { class: "mb-6" }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_col, {
                                      cols: "12",
                                      md: "6"
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_card, {
                                          variant: "outlined",
                                          class: "pa-3"
                                        }, {
                                          default: _withCtx(() => [
                                            _cache[17] || (_cache[17] = _createElementVNode("div", { class: "text-body-1 font-weight-medium mb-2" }, "Oxígeno Promedio", -1)),
                                            _createElementVNode("div", _hoisted_18, _toDisplayString(deviceMetrics.value.averageOxygen || '-') + "%", 1),
                                            _createVNode(OxygenChart, {
                                              oxygen: deviceMetrics.value.averageOxygen || 18
                                            }, null, 8, ["oxygen"])
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_col, {
                                      cols: "12",
                                      md: "6"
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_card, {
                                          variant: "outlined",
                                          class: "pa-3"
                                        }, {
                                          default: _withCtx(() => [
                                            _cache[18] || (_cache[18] = _createElementVNode("div", { class: "text-body-1 font-weight-medium mb-2" }, "Crecimiento Promedio", -1)),
                                            _createElementVNode("div", _hoisted_19, _toDisplayString(deviceMetrics.value.averageGrowthPercentage) + "% ", 1),
                                            _createElementVNode("div", _hoisted_20, " Basado en " + _toDisplayString(deviceMetrics.value.analyzedMeasurements) + " mediciones analizadas ", 1)
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    })
                                  ]),
                                  _: 1
                                }),
                                (sensorData.value.length > 0)
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                                      _cache[19] || (_cache[19] = _createElementVNode("div", { class: "text-body-1 font-weight-medium mb-3" }, "Tendencias de Temperatura y Oxígeno", -1)),
                                      _createVNode(_component_v_card, {
                                        variant: "outlined",
                                        class: "pa-3"
                                      }, {
                                        default: _withCtx(() => [
                                          _createVNode(TemperatureOxygenChart, {
                                            "sensor-data": sensorData.value,
                                            title: "Temperatura vs Oxígeno",
                                            height: "300px"
                                          }, null, 8, ["sensor-data"])
                                        ]),
                                        _: 1
                                      })
                                    ]))
                                  : _createCommentVNode("", true),
                                _cache[30] || (_cache[30] = _createElementVNode("div", { class: "text-h6 mt-6 mb-4" }, "Mediciones de Crecimiento Recientes", -1)),
                                (growthMeasurements.value.length > 0)
                                  ? (_openBlock(), _createBlock(_component_v_table, {
                                      key: 1,
                                      density: "compact"
                                    }, {
                                      default: _withCtx(() => [
                                        _cache[20] || (_cache[20] = _createElementVNode("thead", null, [
                                          _createElementVNode("tr", null, [
                                            _createElementVNode("th", null, "Dispositivo"),
                                            _createElementVNode("th", null, "Crecimiento"),
                                            _createElementVNode("th", null, "Salud"),
                                            _createElementVNode("th", null, "Raíces"),
                                            _createElementVNode("th", null, "Fecha")
                                          ])
                                        ], -1)),
                                        _createElementVNode("tbody", null, [
                                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(growthMeasurements.value, (m, i) => {
                                            return (_openBlock(), _createElementBlock("tr", { key: i }, [
                                              _createElementVNode("td", null, _toDisplayString(m.deviceName), 1),
                                              _createElementVNode("td", null, [
                                                _createVNode(_component_v_chip, {
                                                  size: "small",
                                                  color: m.growthPercentage > 3 ? 'success' : m.growthPercentage > 1 ? 'warning' : 'default'
                                                }, {
                                                  default: _withCtx(() => [
                                                    _createTextVNode(_toDisplayString(m.growthPercentage) + "% ", 1)
                                                  ]),
                                                  _: 2
                                                }, 1032, ["color"])
                                              ]),
                                              _createElementVNode("td", null, _toDisplayString(m.healthScore) + "/10", 1),
                                              _createElementVNode("td", null, _toDisplayString(m.newRoots), 1),
                                              _createElementVNode("td", null, _toDisplayString(m.date), 1)
                                            ]))
                                          }), 128))
                                        ])
                                      ]),
                                      _: 1
                                    }))
                                  : (_openBlock(), _createElementBlock("div", _hoisted_22, [
                                      _createVNode(_component_v_icon, {
                                        size: "32",
                                        color: "grey-lighten-2"
                                      }, {
                                        default: _withCtx(() => _cache[21] || (_cache[21] = [
                                          _createTextVNode("mdi-chart-line")
                                        ])),
                                        _: 1
                                      }),
                                      _cache[22] || (_cache[22] = _createElementVNode("div", { class: "text-body-2 text-medium-emphasis mt-2" }, "Sin mediciones analizadas aún", -1))
                                    ])),
                                _createElementVNode("div", _hoisted_23, [
                                  _createVNode(_component_v_row, null, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_v_col, {
                                        cols: "6",
                                        md: "3"
                                      }, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_v_card, {
                                            variant: "outlined",
                                            class: "pa-3 text-center"
                                          }, {
                                            default: _withCtx(() => [
                                              _cache[23] || (_cache[23] = _createElementVNode("div", { class: "text-body-2 text-medium-emphasis" }, "Tasa Análisis", -1)),
                                              _createElementVNode("div", _hoisted_24, _toDisplayString(deviceMetrics.value.totalMeasurements > 0
                                                        ? Math.round(deviceMetrics.value.analyzedMeasurements / deviceMetrics.value.totalMeasurements * 100)
                                                        : 0) + "% ", 1)
                                            ]),
                                            _: 1
                                          })
                                        ]),
                                        _: 1
                                      }),
                                      _createVNode(_component_v_col, {
                                        cols: "6",
                                        md: "3"
                                      }, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_v_card, {
                                            variant: "outlined",
                                            class: "pa-3 text-center"
                                          }, {
                                            default: _withCtx(() => [
                                              _cache[24] || (_cache[24] = _createElementVNode("div", { class: "text-body-2 text-medium-emphasis" }, "Crec. Promedio", -1)),
                                              _createElementVNode("div", _hoisted_25, _toDisplayString(deviceMetrics.value.averageGrowthPercentage) + "% ", 1)
                                            ]),
                                            _: 1
                                          })
                                        ]),
                                        _: 1
                                      }),
                                      _createVNode(_component_v_col, {
                                        cols: "6",
                                        md: "3"
                                      }, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_v_card, {
                                            variant: "outlined",
                                            class: "pa-3 text-center"
                                          }, {
                                            default: _withCtx(() => [
                                              _cache[25] || (_cache[25] = _createElementVNode("div", { class: "text-body-2 text-medium-emphasis" }, "Total Mediciones", -1)),
                                              _createElementVNode("div", _hoisted_26, _toDisplayString(deviceMetrics.value.totalMeasurements), 1)
                                            ]),
                                            _: 1
                                          })
                                        ]),
                                        _: 1
                                      }),
                                      _createVNode(_component_v_col, {
                                        cols: "6",
                                        md: "3"
                                      }, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_v_card, {
                                            variant: "outlined",
                                            class: "pa-3 text-center"
                                          }, {
                                            default: _withCtx(() => [
                                              _cache[26] || (_cache[26] = _createElementVNode("div", { class: "text-body-2 text-medium-emphasis" }, "Raíces Detectadas", -1)),
                                              _createElementVNode("div", _hoisted_27, _toDisplayString(deviceMetrics.value.totalNewRoots), 1)
                                            ]),
                                            _: 1
                                          })
                                        ]),
                                        _: 1
                                      })
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _createElementVNode("div", _hoisted_28, [
                                  _createVNode(_component_v_icon, {
                                    size: "small",
                                    class: "mr-2"
                                  }, {
                                    default: _withCtx(() => _cache[27] || (_cache[27] = [
                                      _createTextVNode("mdi-information")
                                    ])),
                                    _: 1
                                  }),
                                  _cache[28] || (_cache[28] = _createTextVNode(" Los reportes PDF se generan con los datos actuales del sistema. Cada reporte incluye tablas detalladas, métricas y análisis basados en las mediciones reales de los dispositivos. "))
                                ])
                              ]))
                      ])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})